<template>
  <div class="wt-asset-preview-inline-transcription">
    <div class="transcript-header">
      <div class="transcript-header-tools">
        <div
          class="-language-list"
          v-if="selectedLanguageId && !showSearch"
          :class="{ '-disabled': transcribing }"
        >
          <select
            class="underline normal"
            v-model="selectedLanguageId"
            @change="translate"
            :disabled="transcribing"
          >
            <option
              v-for="item in languageList"
              :key="item.id"
              :value="item.id"
            >
              {{ item.label }}
            </option>
          </select>
        </div>

        <div class="transcript-actions" v-if="!transcribing && transcription">
          <i
            class="wticon wticon-magnifier search-button wt2-color-link"
            :class="{
              '-disabled': !transcription || transcribing || failedToCreate,
            }"
            title="Search Transcript"
            v-if="!showSearch"
            @click="toggleSearch"
          ></i>
        </div>
      </div>

      <div v-if="showSearch" class="search-input-container">
        <input
          ref="searchQuery"
          v-model="searchQuery"
          placeholder="Search transcript..."
          @keydown.stop="handleKeydownSearch"
          @keypress.enter="$refs.transcribeSegment.nextSearchResult"
        />
        <div class="-search-controls">
          <i
            class="wticon wticon-chevron-up search-action"
            :class="{
              '-disabled': !numberOfSearchResults,
            }"
            @click="$refs.transcribeSegment.previousSearchResult"
            :title="numberOfSearchResults ? 'Previous' : ''"
          ></i>
          <i
            class="wticon wticon-chevron-down search-action"
            :class="{
              '-disabled': !numberOfSearchResults,
            }"
            @click="$refs.transcribeSegment.nextSearchResult"
            :title="numberOfSearchResults ? 'Next' : ''"
          ></i>
          <i
            class="wticon wticon-cross3 search-action"
            @click="toggleSearch"
            title="Close"
          ></i>
        </div>
      </div>
      <div class="-processing-panel" v-if="transcribing">
        <div class="-process-indicator">
          <transcribe-animation />
          <template v-if="transcribing === 'translating'">
            Translating
          </template>
          <template v-else>Transcribing</template>
          <wt-ellipsis-animation />
        </div>
      </div>
    </div>

    <div class="-failed-panel" v-if="failedToCreate">
      Failed to create transcript
    </div>

    <div v-if="!transcribing && transcription" class="transcript-content">
      <transcribe-segment
        ref="transcribeSegment"
        :playbackTime="playbackTime"
        :scrollOnPlayback="true"
        :transcript="transcription"
        :isSingleSegment="true"
        :segment="{}"
        :isFirst="true"
        :guid="list.guid"
        :containerScrollTop="0"
        :isActive="true"
        :mode="'view'"
        @changeTime="changeTime"
        @rangeSelection="rangeSelection"
        @onCurrentSearchIndexChange="onCurrentSearchIndexChange"
      />
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import wtTranscribe from 'v/Transcribe/Transcribe.vue';
import TranscribeAnimation from 'AssetEditor/functionality/Animation/TranscribeAnimation.vue';
import { workPool } from '@wochitnpm/helpers';
import TranscribeSegment from 'v/Transcribe/components/TranscribeSegment.vue';

export default {
  name: 'AssetPreviewInlineTranscription',
  components: {
    TranscribeAnimation,
    TranscribeSegment,
  },

  props: {
    asset: { type: Object, required: true },
    playbackTime: { type: [Number], required: false, default: null },
  },

  data() {
    return {
      transcribing: false,
      transcription: null,
      failedToCreate: false,
      workPoolHandlers: new Set(),
      showSearch: false,
      searchQuery: '',
      numberOfSearchResults: 0,
      currentSearchIndex: 0,
      selectedLanguageId: '',
      languageList: [],
    };
  },

  created() {
    this.languageList = app.transcriptionLanguageList();
  },

  mounted() {
    this.failedToCreate = false;
    const existingTranscriptions = app.rootStore.state.story.transcriptions;
    if (
      existingTranscriptions &&
      existingTranscriptions[this.asset.metadata.assetUUID]
    ) {
      this.transcription =
        existingTranscriptions[this.asset.metadata.assetUUID];
    } else {
      this.transcription = null;
      this.transcribing = 'transcribing';
      workPool.job.transcriptions.execute({
        asset: this.asset,
        transcribeSubType: app.transcribeSubType.VIDEO,
        how: workPool.job.transcriptions.HOW.READ_OR_CREATE,
        programmerAllowedTranscription: true,
        alertOnError: false,
      });
      workPool.on(
        {
          type: workPool.job.transcriptions.type,
          context: (jc) => jc.assetUUID === this.asset.metadata.assetUUID,
        },
        (payload) => {
          if (payload.failedToCreate) {
            this.$log('Failed to create transcription:', payload);
            this.failedToCreate = true;
            this.transcribing = false;
          } else if (payload.processing) {
            this.transcribing = true;
          } else {
            if (payload.originalTranscription) {
              this.transcription = payload.originalTranscription;
            } else {
              this.transcription = payload;
            }
            this.transcribing = false;
          }
        },
        this.workPoolHandlers
      );
    }
  },

  beforeDestroy() {
    workPool.off(this.workPoolHandlers);
  },

  computed: {
    ...mapState('assetEditor', ['openedFrom']),
    ...mapState('story', ['transcriptions']),

    selectedLanguagecode() {
      transcription.languageCode;
    },

    list() {
      return {
        assetUUID: this.$props.asset.metadata.assetUUID,
        transcribeSubType: app.transcribeSubType.VIDEO,
        guid: this.$props.asset.metadata.assetUUID,
      };
    },
  },

  watch: {
    searchQuery() {
      this.handleSearch();
    },
    transcription: {
      handler() {
        const transcriptionLanguage = this.transcription?.languageCode;
        if (!transcriptionLanguage) {
          return 'unknown';
        }
        if (
          this.languageList.some(
            (language) => language.id === transcriptionLanguage
          )
        ) {
          this.selectedLanguageId = transcriptionLanguage;
        } else {
          if (transcriptionLanguage.includes('-')) {
            this.selectedLanguageId = transcriptionLanguage.split('-')[0];
          } else {
            this.selectedLanguageId = 'unknown';
          }
        }
      },
      deep: true,
    },
  },

  methods: {
    getTranscriptionCode() {
      return this.transcription?.languageCode;
    },

    translate() {
      if (this.showSearch) {
        this.toggleSearch();
      }
      this.transcription = null;
      this.transcribing = 'translating';
      this.failedToCreate = false;
      workPool.job.transcriptions.execute({
        asset: this.asset,
        transcribeSubType: app.transcribeSubType.VIDEO,
        how: workPool.job.transcriptions.HOW.READ_OR_CREATE,
        storyId: this.$store.state.story.id,
        languageCode: this.selectedLanguageId,
        programmerAllowedTranscription: true,
        alertOnError: false,
      });
      workPool.on(
        {
          type: workPool.job.transcriptions.type,
          context: (jc) => jc.assetUUID === this.asset.metadata.assetUUID,
        },
        (payload) => {
          if (payload.failedToCreate) {
            this.$log('Failed to create transcription:', payload);
            this.failedToCreate = true;
            this.transcribing = false;
          } else if (payload.processing) {
            this.transcribing = true;
          } else {
            this.transcription = payload;
            this.transcribing = false;
          }
        },
        this.workPoolHandlers
      );
    },

    onCurrentSearchIndexChange(searchIndex) {
      this.currentSearchIndex = searchIndex;
    },

    handleKeydownSearch(e) {
      e.stopImmediatePropagation();
      e.stopPropagation();
      if (e.key === 'Escape') {
        this.toggleSearch();
        e.target.blur(); // Add this line to blur the focus
      }
    },

    toggleSearch() {
      this.showSearch = !this.showSearch;
      if (!this.showSearch) {
        this.searchQuery = '';
        this.numberOfSearchResults = 0;
      } else {
        this.$nextTick(() => {
          this.$refs.searchQuery.focus();
        });
      }
    },

    handleSearch() {
      if (!this.searchQuery.length) {
        this.$refs.transcribeSegment.search(null);
        this.numberOfSearchResults = 0;
        return;
      }
      this.numberOfSearchResults = this.$refs.transcribeSegment.search(
        this.searchQuery
      );
    },

    selectByTimestamps(...args) {
      if (this.$refs.transcribeSegment) {
        this.$refs.transcribeSegment.selectByTimestamps(...args);
      }
    },

    changeTime(time) {
      this.$emit('seek-preview-video', time);
    },

    rangeSelection(rangeSelection) {
      this.$emit('range-selection', rangeSelection);
    },
  },
};
</script>

<style lang="scss" scoped>
.wt-asset-preview-inline-transcription {
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  // height: 100%; // Ensure the component takes full height of its container

  .transcript-header {
    position: sticky;
    top: 35px;
    height: 35px;
    padding: 5px 0;
    background-color: #f7f7f7;
    z-index: 2;
    padding: 0;
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
    flex-direction: column;
    .transcript-header-tools {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
    }
  }

  .transcript-actions {
    display: flex;
    align-items: center;
  }

  .search-button {
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    color: inherit;

    &.-active {
      color: #06a9f4;
    }

    &.-disabled {
      opacity: 0.5;
      cursor: not-allowed;
      pointer-events: none;
    }
  }

  .search-input-container {
    position: absolute;
    top: 0;
    width: 100%;
    background-color: #f7f7f7;
    z-index: 2;

    input {
      width: 100%;
      padding: 4px 8px;
      border: 1px solid #ccc;
      border-radius: 4px;
      height: 30px;
      padding-right: 80px;
      font-size: 13px;
    }

    .-search-controls {
      position: absolute;
      right: 0;
      top: 12px;
      right: 5px;
      font-size: 12px;
      transform: translateY(-50%);
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: 4px;
      border-left: 1px solid #bababa;
      padding-left: 5px;
      .search-action {
        font-weight: 600;
        background: none;
        border: none;
        cursor: pointer;
        padding: 4px;
        color: inherit;
        padding-left: 5px;
        padding-right: 5px;
        &.-disabled {
          opacity: 0.5;
          pointer-events: none;
        }
        .wticon-chevron-up,
        .wticon-chevron-down {
          font-size: 14px;
        }
      }
    }
  }

  .-processing-panel {
    position: relative;
    display: flex;
    padding-top: 10px;

    .-process-indicator {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-right: 15px;

      .wt-transcribe-animation {
        margin-right: 5px;
      }
    }
  }

  .transcript-content {
    // overflow-y: auto;
    // flex-grow: 1;
    // margin-top: 10px;
  }
}

.-language-list {
  display: inline-block;
  position: relative;
  width: 150px;
  font-size: 14px;
  &.-disabled {
    opacity: 0.5;
    pointer-events: none;
  }
  select {
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    width: 100%;
    padding: 3px 20px 3px 2px;
    font-weight: 300;
    text-align: left;
    background-color: transparent;
    border: none;
    border-bottom: 1px solid #ccc;
    cursor: pointer;
    font-size: 13px;
    color: inherit;
    text-overflow: ellipsis;

    &:hover,
    &:focus {
      // color: $wt-blue-light !important;
      background: white;
      border-bottom-color: $wt-blue-light !important;
      outline: 0 !important;
      box-shadow: none;
    }

    option {
      padding: 8px 10px;
      font-size: 14px;
      font-weight: 400;
      background-color: $white;
      color: $dark-text-color;

      &:checked {
        color: $wt-blue-light;
        background-color: $white;
      }

      &:hover {
        background-color: $white;
        text-shadow: 0.1px 0.1px #ccc;
      }
    }
  }

  &::after {
    content: '';
    position: absolute;
    right: 7px;
    top: 50%;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-top: 4px solid #666;
    pointer-events: none;
    transition: transform 0.3s;
  }

  select:focus + &::after {
    transform: translateY(-50%) rotate(180deg);
  }
}
</style>
